import React from "react";
import { keyframes } from "@emotion/react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import Iconify from "./Iconify";
import useAutoPlayLoop from "../hooks/useAutoPlayLoop";
import { ctaGlowSx, EASE_OUT } from "../theme/brand";

// Rein dekorative Produkt-Demo (keine echten Daten) – ersetzt ein
// aufgenommenes Video durch eine kleine, endlos laufende UI-Animation.
const ROWS = [
  { name: "Lena Fischer", belt: "#f03e3e" },
  { name: "Noah Keller", belt: "#1a1b1e" },
  { name: "Mia Brandt", belt: "#339af0" },
];

const STEPS = [
  { phase: "preview", doneCount: 0, duration: 1700 },
  { phase: "confirming", doneCount: 0, duration: 650 },
  { phase: "booking", doneCount: 1, duration: 420 },
  { phase: "booking", doneCount: 2, duration: 420 },
  { phase: "booking", doneCount: 3, duration: 420 },
  { phase: "done", doneCount: 3, duration: 2400 },
];

const pop = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.18); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const CHIP_STYLES = {
  preview: { label: "Vorschau", bg: "rgba(139,127,234,0.12)", color: "#5b4fd4" },
  confirming: { label: "Bestätigt", bg: "rgba(51,154,240,0.12)", color: "#1c7ed6" },
  booking: { label: "Wird abgebucht …", bg: "rgba(51,154,240,0.12)", color: "#1c7ed6" },
  done: { label: "Abgebucht", bg: "rgba(43,138,62,0.12)", color: "#2b8a3e" },
};

export default function PaymentRunAnimation({ maxWidth = 420 }) {
  const { ref, step } = useAutoPlayLoop(STEPS);
  const chip = CHIP_STYLES[step.phase];
  const finished = step.phase === "done";

  return (
    <Box
      ref={ref}
      aria-hidden="true"
      sx={{
        bgcolor: "#fff",
        borderRadius: 3,
        boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
        overflow: "hidden",
        width: "100%",
        maxWidth,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid", borderColor: "grey.100" }}
      >
        <Typography variant="subtitle2" component="span" fontWeight={800} color="grey.900">
          Beitragslauf · November
        </Typography>
        <Chip
          size="small"
          label={chip.label}
          sx={{
            bgcolor: chip.bg,
            color: chip.color,
            fontWeight: 700,
            transition: `background-color 300ms ${EASE_OUT}, color 300ms ${EASE_OUT}`,
          }}
        />
      </Stack>

      <Stack spacing={0} sx={{ px: 2.5, py: 1.5 }}>
        {ROWS.map((row, i) => {
          const done = i < step.doneCount;
          return (
            <Stack
              key={row.name}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                py: 1.1,
                px: 1,
                mx: -1,
                borderRadius: 1.5,
                bgcolor: done ? "rgba(43,138,62,0.06)" : "transparent",
                transition: `background-color 400ms ${EASE_OUT}`,
              }}
            >
              <Box sx={{ width: 8, height: 28, borderRadius: 4, bgcolor: row.belt, flexShrink: 0 }} />
              <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1, color: "grey.800" }}>
                {row.name}
              </Typography>
              <Chip
                size="small"
                icon={
                  <Box
                    key={done ? "done" : "pending"}
                    component="span"
                    sx={{ display: "inline-flex", animation: `${pop} 420ms ${EASE_OUT}` }}
                  >
                    <Iconify
                      icon={done ? "mdi:check-circle" : "mdi:clock-outline"}
                      width={14}
                      sx={{ color: done ? "#2b8a3e" : "#868e96" }}
                    />
                  </Box>
                }
                label={done ? "Abgebucht" : "Fällig"}
                sx={{
                  bgcolor: done ? "rgba(43,138,62,0.1)" : "grey.100",
                  color: done ? "#2b8a3e" : "grey.600",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
            </Stack>
          );
        })}
      </Stack>

      <Box sx={{ px: 2.5, pb: 2.25 }}>
        <Box
          sx={{
            ...ctaGlowSx,
            py: 1,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          <Iconify icon={finished ? "mdi:check-bold" : "mdi:bank-transfer"} width={16} sx={{ color: "#fff" }} />
          {finished ? "3 Beiträge digital abgebucht" : "Direkt digital abbuchen"}
        </Box>
        <Typography
          variant="caption"
          sx={{ display: "block", textAlign: "center", color: "grey.500", mt: 1 }}
        >
          {finished ? "Automatisch eingereicht – fertig." : "Keine SEPA-Datei nötig."}
        </Typography>
      </Box>
    </Box>
  );
}
